import { useState } from 'react';
import {
  Terminal,
  Braces,
  Package,
  ChevronDown,
  ChevronRight,
  Shield,
  FolderCog,
  Hash,
  Workflow,
  Download,
  Zap,
  BookOpen,
} from 'lucide-react';
import clsx from 'clsx';
import { CodeBlock } from '@/components/CodeBlock';
import { CLI_DOC } from '@/docs/cli';

type DocTopic = 'cli' | 'api' | 'sdk';

const TOPICS = [
  {
    id: 'cli' as DocTopic,
    title: 'CLI',
    subtitle: 'Command-line interface for terminal workflows',
    icon: Terminal,
    gradient: 'from-blue-600 via-blue-500 to-cyan-400',
    available: true,
  },
  {
    id: 'api' as DocTopic,
    title: 'REST API',
    subtitle: 'HTTP API reference for service integration',
    icon: Braces,
    gradient: 'from-violet-600 via-purple-500 to-fuchsia-400',
    available: false,
  },
  {
    id: 'sdk' as DocTopic,
    title: 'SDK',
    subtitle: 'Client libraries for Python and TypeScript',
    icon: Package,
    gradient: 'from-amber-600 via-orange-500 to-yellow-400',
    available: false,
  },
];

export function DocsPage() {
  const [activeTopic, setActiveTopic] = useState<DocTopic>('cli');
  const [expandedCommands, setExpandedCommands] = useState<Set<string>>(new Set());

  const toggleCommand = (name: string) => {
    setExpandedCommands(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      {/* Header */}
      <div className="mb-8 animate-stagger-1">
        <h1 className="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Documentation
        </h1>
        <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
          Developer tools and reference guides
        </p>
      </div>

      {/* Integration selector cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 animate-stagger-2">
        {TOPICS.map((topic) => (
          <button
            key={topic.id}
            onClick={() => topic.available && setActiveTopic(topic.id)}
            disabled={!topic.available}
            className={clsx(
              'relative rounded-fluent-lg overflow-hidden text-left transition-all duration-200',
              'border',
              topic.available && activeTopic === topic.id
                ? 'border-brand-500 ring-1 ring-brand-500/30'
                : 'border-surface-200 dark:border-white/[0.06]',
              topic.available
                ? 'cursor-pointer hover:border-surface-300 dark:hover:border-white/[0.12]'
                : 'opacity-50 cursor-not-allowed'
            )}
          >
            {/* Gradient strip */}
            <div className={clsx(
              'h-1.5 bg-gradient-to-r',
              topic.gradient,
              !topic.available && 'opacity-30'
            )} />

            <div className="p-4 bg-white dark:bg-[#2a2a2a]">
              <div className="flex items-center gap-3 mb-2">
                <div className={clsx(
                  'w-9 h-9 rounded-fluent-md flex items-center justify-center',
                  activeTopic === topic.id && topic.available
                    ? 'bg-brand-500 text-white'
                    : 'bg-surface-100 dark:bg-white/[0.06] text-surface-500 dark:text-surface-400'
                )}>
                  <topic.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                    {topic.title}
                  </p>
                  {!topic.available && (
                    <span className="text-[10px] text-surface-400">Coming soon</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-surface-500 dark:text-surface-400">
                {topic.subtitle}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* CLI Documentation */}
      {activeTopic === 'cli' && (
        <div className="space-y-8 animate-stagger-3">
          {/* ─── Installation ─── */}
          <section>
            <SectionHeader icon={Download} title="Installation" />
            <div className="space-y-3 mt-4">
              {CLI_DOC.installation.steps.map((step, i) => (
                <div key={i}>
                  <p className="text-xs text-surface-500 dark:text-surface-400 mb-1.5 font-medium">
                    {step.title}
                  </p>
                  <CodeBlock code={step.code} language="shell" />
                </div>
              ))}
              <p className="text-xs text-surface-400 italic mt-2">
                {CLI_DOC.installation.note}
              </p>
            </div>
          </section>

          {/* ─── Quick Start ─── */}
          <section>
            <SectionHeader icon={Zap} title="Quick Start" />
            <div className="mt-4 space-y-0">
              {CLI_DOC.quickStart.map((step, i) => (
                <div key={i} className="flex gap-4">
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </div>
                    {i < CLI_DOC.quickStart.length - 1 && (
                      <div className="w-px flex-1 bg-surface-200 dark:bg-white/[0.06] my-1" />
                    )}
                  </div>
                  {/* Content */}
                  <div className="flex-1 pb-6">
                    <h4 className="text-sm font-medium text-surface-900 dark:text-surface-100 mb-0.5">
                      {step.title}
                    </h4>
                    {step.description && (
                      <p className="text-xs text-surface-500 dark:text-surface-400 mb-2">
                        {step.description}
                      </p>
                    )}
                    <CodeBlock code={step.code} language="shell" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Global Options ─── */}
          <section>
            <SectionHeader icon={FolderCog} title="Global Options" />
            <div className="mt-4 card overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>Option</th>
                    <th>Alias</th>
                    <th>Description</th>
                    <th>Default</th>
                  </tr>
                </thead>
                <tbody>
                  {CLI_DOC.globalOptions.map((opt) => (
                    <tr key={opt.option}>
                      <td className="font-mono text-xs text-brand-500">{opt.option}</td>
                      <td className="font-mono text-xs text-surface-400">{opt.alias || ''}</td>
                      <td className="text-xs">{opt.description}</td>
                      <td className="font-mono text-xs text-surface-400">{opt.default || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Command Reference ─── */}
          <section>
            <SectionHeader icon={BookOpen} title="Command Reference" />
            <div className="mt-4 space-y-2">
              {CLI_DOC.commands.map((cmd) => {
                const isExpanded = expandedCommands.has(cmd.name);
                return (
                  <div key={cmd.name} className="card overflow-hidden">
                    {/* Command header — clickable */}
                    <button
                      onClick={() => toggleCommand(cmd.name)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-50 dark:hover:bg-white/[0.03] transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-surface-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-surface-400 flex-shrink-0" />
                      )}
                      <code className="text-sm font-mono font-semibold text-brand-500">{cmd.name}</code>
                      <span className="text-xs text-surface-500 dark:text-surface-400 flex-1">
                        {cmd.description}
                      </span>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 border-t border-surface-100 dark:border-white/[0.04] space-y-4">
                        {/* Usage */}
                        <CodeBlock code={cmd.usage} language="shell" />

                        {/* Options table */}
                        {cmd.options && cmd.options.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-surface-500 dark:text-surface-400 mb-2">Options</p>
                            <div className="overflow-x-auto">
                              <table className="table text-xs">
                                <thead>
                                  <tr>
                                    <th>Option</th>
                                    <th>Description</th>
                                    <th>Default</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cmd.options.map((opt) => (
                                    <tr key={opt.option}>
                                      <td className="font-mono text-brand-500 whitespace-nowrap">
                                        {opt.option}{opt.alias ? `, ${opt.alias}` : ''}
                                      </td>
                                      <td>{opt.description}</td>
                                      <td className="font-mono text-surface-400">{opt.default || ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Examples */}
                        {cmd.examples && cmd.examples.length > 0 && (
                          <div className="space-y-3">
                            <p className="text-xs font-medium text-surface-500 dark:text-surface-400">Examples</p>
                            {cmd.examples.map((ex, i) => (
                              <div key={i}>
                                <p className="text-xs text-surface-400 mb-1.5">{ex.description}</p>
                                <CodeBlock code={ex.code} language="shell" />
                                {ex.output && (
                                  <div className="mt-2">
                                    <CodeBlock code={ex.output} title="Output" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ─── Workflows ─── */}
          <section>
            <SectionHeader icon={Workflow} title="End-to-End Workflows" />
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {CLI_DOC.workflows.map((wf) => (
                <div key={wf.title} className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
                    <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                      {wf.title}
                    </h4>
                    <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                      {wf.description}
                    </p>
                  </div>
                  <CodeBlock code={wf.code} language="shell" />
                </div>
              ))}
            </div>
          </section>

          {/* ─── Authentication ─── */}
          <section>
            <SectionHeader icon={Shield} title="Authentication" />
            <div className="mt-4">
              <p className="text-sm text-surface-500 dark:text-surface-400 mb-3">
                {CLI_DOC.auth.description}
              </p>
              <div className="space-y-2">
                {CLI_DOC.auth.methods.map((method, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-6 h-6 rounded-full bg-brand-500/10 text-brand-500 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-surface-900 dark:text-surface-100 font-mono">
                        {method.name}
                      </span>
                      <span className="text-sm text-surface-500 dark:text-surface-400 ml-2">
                        — {method.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ─── Reference tables ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Config paths */}
            <section>
              <SectionHeader icon={FolderCog} title="Config Paths" />
              <div className="mt-4 card overflow-hidden">
                <table className="table text-xs">
                  <thead>
                    <tr><th>Platform</th><th>Path</th></tr>
                  </thead>
                  <tbody>
                    {CLI_DOC.configPaths.map((cp) => (
                      <tr key={cp.platform}>
                        <td className="font-medium">{cp.platform}</td>
                        <td className="font-mono text-surface-400">{cp.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Exit codes */}
            <section>
              <SectionHeader icon={Hash} title="Exit Codes" />
              <div className="mt-4 card overflow-hidden">
                <table className="table text-xs">
                  <thead>
                    <tr><th>Code</th><th>Meaning</th></tr>
                  </thead>
                  <tbody>
                    {CLI_DOC.exitCodes.map((ec) => (
                      <tr key={ec.code}>
                        <td className="font-mono font-medium">{ec.code}</td>
                        <td>{ec.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* Placeholder for future topics */}
      {activeTopic === 'api' && (
        <div className="empty-state animate-fade-in">
          <Braces className="w-10 h-10 text-surface-400 mb-3" />
          <h3 className="text-base font-semibold text-surface-900 dark:text-surface-100">API Reference</h3>
          <p className="text-sm text-surface-500 mt-1">Coming soon.</p>
        </div>
      )}

      {activeTopic === 'sdk' && (
        <div className="empty-state animate-fade-in">
          <Package className="w-10 h-10 text-surface-400 mb-3" />
          <h3 className="text-base font-semibold text-surface-900 dark:text-surface-100">SDK Libraries</h3>
          <p className="text-sm text-surface-500 mt-1">Coming soon.</p>
        </div>
      )}
    </div>
  );
}

/** Reusable section header with icon */
function SectionHeader({ icon: Icon, title }: { icon: React.ComponentType<{ className?: string }>; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-brand-500" />
      <h3 className="text-base font-semibold text-surface-900 dark:text-surface-100">
        {title}
      </h3>
    </div>
  );
}
